import React, { useCallback, useEffect, useRef, useState } from 'react';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { Toast } from 'primereact/toast';
import { EstadoTag, hora, poli, subirArchivo, urlArchivo } from './poliApi';
import { Dialog } from 'primereact/dialog';

/**
 * P-5 Recepcion: agenda del dia, verificacion de identidad y asistencia. Solo trae lo necesario
 * para recibir a la persona (sin motivo, grupo, historial ni informe: el servidor no lo envia).
 * La asistencia solo se registra en las citas de hoy. Las fotos de identificacion que se toman aca
 * pasan a ser las oficiales de la prueba; se cifran en el backend antes de guardarse (S-7).
 */
const hoy = () => {
    const d = new Date();
    const z = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${z(d.getMonth() + 1)}-${z(d.getDate())}`;
};

const PoliRecepcion = () => {
    const toast = useRef(null);
    const [dia, setDia] = useState(hoy());
    const [agenda, setAgenda] = useState([]);
    const [fotos, setFotos] = useState([]);
    const [verificado, setVerificado] = useState({});
    const [cargando, setCargando] = useState(false);
    const [tomadas, setTomadas] = useState({});
    const [visor, setVisor] = useState(null);

    const avisar = (severity, detail) => toast.current && toast.current.show({ severity, summary: severity === 'error' ? 'Error' : 'Registrado', detail, life: 3500 });

    const cargar = useCallback(async (d = dia) => {
        setCargando(true);
        try {
            const lista = await poli(`/recepcion/agenda?fecha=${d}`);
            setAgenda(lista);
            if (d === hoy()) {
                const t = {};
                await Promise.all(lista.filter((a) => [2, 4, 5].includes(a.estado)).map(async (a) => {
                    try { t[a.prueba_id] = await poli(`/pruebas/${a.prueba_id}/archivos`); } catch (e) { t[a.prueba_id] = []; }
                }));
                setTomadas(t);
            }
        } catch (e) {
            avisar('error', e.message);
        } finally {
            setCargando(false);
        }
    }, [dia]);

    useEffect(() => {
        cargar();
        poli('/config').then((c) => setFotos(c.catalogos.filter((x) => x.tipo === 'FOTO' && x.activo))).catch(() => setFotos([]));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const marcar = async (a, estado) => {
        try {
            await poli(`/pruebas/${a.prueba_id}/estado`, 'POST', { estado });
            avisar('success', `${a.nombres} ${a.apellidos}: ${estado === 5 ? 'asistio' : 'no asistio'}`);
            cargar();
        } catch (e) {
            avisar('error', e.message);
        }
    };

    const tomarFoto = async (a, etiqueta, e) => {
        const f = e.target.files && e.target.files[0];
        e.target.value = '';
        if (!f) return;
        try {
            await subirArchivo(a.prueba_id, f, 'FOTO', etiqueta);
            avisar('success', `${etiqueta} guardada`);
            setTomadas({ ...tomadas, [a.prueba_id]: await poli(`/pruebas/${a.prueba_id}/archivos`) });
        } catch (err) {
            avisar('error', err.message);
        }
    };

    const verFoto = async (archivo) => {
        try {
            setVisor({ url: await urlArchivo(archivo.archivo_id), titulo: archivo.etiqueta });
        } catch (e) {
            avisar('error', e.message);
        }
    };

    const esHoy = dia === hoy();

    return (
        <div className="card">
            <Toast ref={toast} />
            <div className="flex align-items-end justify-content-between flex-wrap gap-2 mb-3">
                <h4 className="m-0">Recepcion · agenda del dia</h4>
                <div className="flex align-items-end gap-2">
                    <div className="flex flex-column gap-1">
                        <label htmlFor="rec-dia">Dia</label>
                        <InputText id="rec-dia" type="date" value={dia} onChange={(e) => { setDia(e.target.value); cargar(e.target.value); }} />
                    </div>
                    <Button icon="pi pi-refresh" className="p-button-outlined" aria-label="Actualizar" onClick={() => cargar()} loading={cargando} />
                </div>
            </div>
            {!esHoy && <p className="text-600">La asistencia solo se registra en las citas de hoy; este dia es de consulta.</p>}
            {agenda.length === 0 && !cargando && <p className="text-600">No hay citas para este dia.</p>}
            <div className="flex flex-column gap-2">
                {agenda.map((a) => {
                    const pendiente = a.estado === 2 || a.estado === 4;
                    return (
                        <div key={a.prueba_id} className="border-1 surface-border border-round p-3 flex flex-wrap align-items-center gap-3">
                            <div className="text-2xl font-bold" style={{ minWidth: '4.5rem', fontVariantNumeric: 'tabular-nums' }}>{hora(a.fecha_asignada)}</div>
                            <div className="flex-1" style={{ minWidth: '14rem' }}>
                                <div className="font-bold">{a.apellidos} {a.nombres}</div>
                                <div className="text-600">{a.cedula ? `C.I. ${a.cedula} · ` : ''}{a.compania} · {a.codigo}</div>
                                {a.asistencia_fecha && <small className="text-600">Llego a las {hora(a.asistencia_fecha)}</small>}
                            </div>
                            <EstadoTag fila={a} />
                            {esHoy && [2, 4, 5].includes(a.estado) && fotos.length > 0 && (
                                <div className="w-full flex flex-wrap gap-2">
                                    {fotos.map((f) => {
                                        const hecha = (tomadas[a.prueba_id] || []).find((x) => x.tipo === 'FOTO' && x.etiqueta === f.nombre);
                                        const id = `foto-${a.prueba_id}-${f.catalogo_id}`;
                                        return (
                                            <span key={f.catalogo_id} className="flex align-items-center gap-1">
                                                <label htmlFor={id} className={`p-button p-button-sm ${hecha ? 'p-button-success' : 'p-button-outlined'}`} style={{ cursor: 'pointer' }}>
                                                    <i className={hecha ? 'pi pi-check mr-1' : 'pi pi-camera mr-1'} />{f.nombre}
                                                </label>
                                                <input id={id} type="file" accept="image/jpeg,image/png" capture="environment" hidden onChange={(e) => tomarFoto(a, f.nombre, e)} />
                                                {hecha && <button type="button" className="p-link text-sm" onClick={() => verFoto(hecha)}>ver</button>}
                                            </span>
                                        );
                                    })}
                                </div>
                            )}
                            {pendiente && esHoy && (
                                <div className="flex flex-wrap align-items-center gap-2">
                                    <div className="flex align-items-center gap-2">
                                        <Checkbox inputId={`ver-${a.prueba_id}`} checked={!!verificado[a.prueba_id]}
                                            onChange={(e) => setVerificado({ ...verificado, [a.prueba_id]: e.checked })} />
                                        <label htmlFor={`ver-${a.prueba_id}`}>Verifique la cedula y el rostro</label>
                                    </div>
                                    <Button label="Asistio" icon="pi pi-check" className="p-button-success" disabled={!verificado[a.prueba_id]} onClick={() => marcar(a, 5)} />
                                    <Button label="No asistio" icon="pi pi-times" className="p-button-danger p-button-outlined" onClick={() => marcar(a, 9)} />
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
            {fotos.length > 0 && (
                <p className="text-600 mt-3 mb-0">
                    Fotos de identificacion: {fotos.map((f) => f.nombre).join(', ')}. En el celular o la tableta se abre la camara; se guardan cifradas.
                </p>
            )}
            <Dialog header={visor ? visor.titulo : ''} visible={!!visor} style={{ width: 'min(40rem, 95vw)' }}
                onHide={() => { if (visor) URL.revokeObjectURL(visor.url); setVisor(null); }}>
                {visor && <img src={visor.url} alt={visor.titulo} style={{ maxWidth: '100%' }} />}
            </Dialog>
        </div>
    );
};

export default PoliRecepcion;
